import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy | IntelliEats';
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-530">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-531">
        <header className="mb-12" data-id="element-532">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-533">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-534" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6" data-id="element-535">
            <div className="bg-brand-100 text-brand-600 p-3 rounded-2xl" data-id="element-536">
              <Shield className="w-8 h-8" data-id="element-537" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight" data-id="element-538">
              Privacy Policy
            </h1>
          </div>
          <p className="text-slate-500 text-sm" data-id="element-539">Last updated: April 7, 2025</p>
        </header>

        <div className="space-y-10 text-slate-700 leading-relaxed" data-id="element-540">
          <section data-id="element-541">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-542">
              1. Introduction
            </h2>
            <p data-id="element-543">
              Welcome to IntelliEats ("we," "our," or "us"). We are committed to
              protecting your personal information and your right to privacy.
              This Privacy Policy explains how we collect, use, disclose, and
              safeguard your information when you use our mobile application
              IntelliEats (the "App").
            </p>
          </section>

          <section data-id="element-544">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-545">
              2. Information We Collect
            </h2>
            <div className="space-y-4" data-id="element-546">
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-547">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-548">
                  Camera & Photo Access
                </h3>
                <p className="m-0" data-id="element-549">
                  We access your device camera and photo gallery solely to scan
                  ingredient labels. Images are processed locally on your device
                  for analysis and are not stored
                  permanently.
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-550">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-551">Usage Data</h3>
                <p className="m-0" data-id="element-552">
                  We may collect anonymous usage data such as app interactions,
                  scan frequency, and feature usage to improve the App's
                  performance and user experience.
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-553">
                <h3 className="font-bold text-slate-900 mb-2" data-id="element-554">
                  Device Information
                </h3>
                <p className="m-0" data-id="element-555">
                  We may collect device type, operating system version, and
                  unique device identifiers for analytics and troubleshooting
                  purposes.
                </p>
              </div>
            </div>
          </section>

          <section data-id="element-556">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-557">
              3. How We Use Your Information
            </h2>
            <ul className="space-y-3 list-none p-0" data-id="element-558">
              <li className="flex items-start gap-3" data-id="element-559">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-560"></span>
                <span data-id="element-561">
                  To provide and maintain the ingredient scanning and analysis
                  service
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-562">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-563"></span>
                <span data-id="element-564">
                  To improve, personalize, and expand our App's features
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-565">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-566"></span>
                <span data-id="element-567">To understand and analyze how you use our App</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-568">
                <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-569"></span>
                <span data-id="element-570">
                  To communicate with you for customer service and updates
                </span>
              </li>
            </ul>
          </section>

          <section data-id="element-571">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-572">
              4. Data Sharing & Third Parties
            </h2>
            <p data-id="element-573">
              We do not sell, trade, or rent your personal information to third
              parties. We may share anonymized, aggregated data with analytics
              partners to improve our services. Any third-party services used
              (such as analytics tools) are bound by their own privacy policies.
            </p>
          </section>

          <section data-id="element-574">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-575">
              5. Data Security
            </h2>
            <p data-id="element-576">
              We implement appropriate technical and organizational security
              measures to protect your personal information. However, no method
              of transmission over the Internet or electronic storage is 100%
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section data-id="element-577">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-578">
              6. Children's Privacy
            </h2>
            <p data-id="element-579">
              Our App is rated for ages 4+ and does not knowingly collect
              personal information from children under 13. If you believe we
              have collected information from a child under 13, please contact
              us immediately.
            </p>
          </section>

          <section data-id="element-580">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-581">
              7. Changes to This Policy
            </h2>
            <p data-id="element-582">
              We may update this Privacy Policy from time to time. We will
              notify you of any changes by posting the new Privacy Policy on
              this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="bg-brand-50 rounded-2xl p-8 border border-brand-100" data-id="element-583">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-584">
              8. Contact Us
            </h2>
            <p className="mb-4" data-id="element-585">
              If you have any questions about this Privacy Policy, please
              contact us:
            </p>
            <p className="font-medium text-slate-900" data-id="element-586">
              Email:{' '}
              <a href="mailto:intellieats@gmail.com" className="text-brand-600 hover:text-brand-700 transition-colors" data-id="element-587">
                intellieats@gmail.com
              </a>
            </p>
            <p className="mt-2" data-id="element-588">
              Or use our{' '}
              <Link to="/contact" className="text-brand-600 hover:text-brand-700 font-medium transition-colors" data-id="element-589">
                Contact Support
              </Link>{' '}
              page.
            </p>
          </section>
        </div>
      </div>
    </motion.div>;
}